import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import ServiceCard from './ServiceCard';
import BookingModal from './BookingModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// Guest data functions
const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();

    // Check if this is a corporate guest
    const isCorporateGuest = profileData.companyid && profileData.companyname;

    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        first_name: profileData.firstname || '',
        last_name: profileData.lastname || '',
        email: profileData.emailid || '',
        phone_number: profileData.phonenumber || '',
        country_code: '+91',
        client_type: profileData.clienttype || 'Leisure',
        company_id: profileData.companyid || '',
        company_name: profileData.companyname || '',
        is_corporate: isCorporateGuest,
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      first_name: profileData.firstname || '',
      last_name: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
      country_code: '+91',
      client_type: profileData.clienttype || 'Leisure',
      company_id: profileData.companyid || '',
      company_name: profileData.companyname || '',
      is_corporate: isCorporateGuest,
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

const RoomServices = () => {
  const [services, setServices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    serviceName: '',
    itemName: '',
    urgencyLevel: 'Normal',
    scheduleTime: '',
    requestedTime: new Date().toISOString().slice(0, 16),
    specialInstruction: '',
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [guestData, setGuestData] = useState(null);
  const [isCorporateGuest, setIsCorporateGuest] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [billingDetails, setBillingDetails] = useState(null);
  const [taxDetails, setTaxDetails] = useState({ value: 0, value_type: 'P' });

  // Fetch booking ID from sessionStorage or localStorage
  useEffect(() => {
    const getBookingId = () => {
      // First check sessionStorage
      let bookingDetails = JSON.parse(sessionStorage.getItem('bookingDetails')) || {};
      let id = bookingDetails.primaryBooking?.booking_id || '';

      // If not found in sessionStorage, check localStorage
      if (!id) {
        bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};
        id = bookingDetails.primaryBooking?.booking_id || '';
        console.log('Booking ID retrieved from localStorage:', id);
      } else {
        console.log('Booking ID retrieved from sessionStorage:', id);
      }

      setBookingId(id);
    };

    getBookingId();
  }, []);

  // Fetch room services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-categories/`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();

        // Transform services to include tax info from API
        const transformedServices = data.map((service) => ({
          ...service,
          // If service has tax info, use it; otherwise use default
          tax_value: service.tax_value || service.value || 0,
          tax_value_type: service.tax_value_type || service.value_type || 'P',
          // For items within the service, also transform
          items: (service.items || []).map((item) => ({
            ...item,
            tax_value: item.tax_value || item.value || 0,
            tax_value_type: item.tax_value_type || item.value_type || 'P',
          })),
        }));

        setServices(transformedServices);
      } catch (error) {
        console.error('Error fetching room services:', error);
      }
    };

    fetchServices();
  }, []);

  // Fetch guest data on component mount
  useEffect(() => {
    const fetchGuestData = async () => {
      const guestInfo = await getGuestData();
      setGuestData(guestInfo);
      setIsCorporateGuest(guestInfo?.is_corporate || false);
    };

    fetchGuestData();
  }, []);

  // Calculate tax based on value and value_type
  const calculateTax = (amount, value, valueType) => {
    if (!value || value === 0) return 0;

    if (valueType === 'P') {
      // Percentage
      return (amount * value) / 100;
    } else if (valueType === 'F') {
      // Fixed amount
      return value;
    }
    return 0;
  };

  const confirmServiceBooking = async () => {
    try {
      if (!bookingId) {
        toast.error('Booking ID not found. Please try again.');
        return;
      }

      // Add category_name to the query parameters
      const url = new URL(`${BQ_BASE_URL}/bq/api/create_room_service_billing`);
      url.searchParams.append('bookingid', bookingId);
      url.searchParams.append('category_name', selectedService.category_name);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([selectedService.roomservice_name]),
      });

      if (response.ok) {
        const data = await response.json();

        // Store tax details from the response
        if (data.tax_value || data.value) {
          setTaxDetails({
            value: data.tax_value || data.value || 0,
            value_type: data.tax_value_type || data.value_type || 'P',
          });
        }

        // CLEAR ANY EXISTING BILLING DATA FIRST
        sessionStorage.removeItem('billingDetails');
        sessionStorage.removeItem('selectedService');

        // THEN SET NEW DATA - ONLY ONE BILLING ID SHOULD EXIST NOW
        sessionStorage.setItem('billingDetails', JSON.stringify(data));
        sessionStorage.setItem('selectedService', JSON.stringify(selectedService));

        setBillingDetails(data);
        console.log('New billing details saved - SINGLE BILLING ID:', {
          billingId: data.billingid,
          amount: data.finalamount,
          taxValue: data.tax_value || data.value,
          taxType: data.tax_value_type || data.value_type,
        });

        toast.success('Service confirmed successfully');
        closeConfirmationModal();
        setShowPaymentModal(true);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail?.[0]?.msg || 'Failed to confirm service.');
      }
    } catch (error) {
      toast.error('An error occurred while confirming the service.');
    }
  };

  const closeConfirmationModal = () => {
    setSelectedService(null);
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  const handlePaymentOption = async (paymentMethod) => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (paymentMethod === 'payNow') {
      try {
        // Get FRESH data from sessionStorage
        const selectedService = JSON.parse(sessionStorage.getItem('selectedService'));
        const billingDetails = JSON.parse(sessionStorage.getItem('billingDetails'));

        if (!selectedService || !billingDetails) {
          toast.error('No active booking found. Please book a service first.');
          return;
        }

        // Log to verify we're using the correct amount and ONLY ONE billing ID
        console.log('Processing payment for CURRENT booking only:', {
          service: selectedService.roomservice_name,
          amount: billingDetails.finalamount,
          billingId: billingDetails.billingid,
          bookingId: bookingId,
          taxValue: billingDetails.tax_value || billingDetails.value,
          taxType: billingDetails.tax_value_type || billingDetails.value_type,
        });

        // Make sure we're ONLY using the current billing ID
        const billingId = billingDetails.billingid;

        const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

        console.log('Creating payment order for single billing ID:', billingId);

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Payment order creation failed:', errorText);
          throw new Error('Failed to create payment order');
        }

        const orderData = await response.json();

        console.log(
          'Order created with amount:',
          orderData.amount,
          'Expected:',
          billingDetails.finalamount * 100
        );

        if (orderData && orderData.razorpay_order_id) {
          const options = {
            key: REACT_APP_RAZORPAY_KEY,
            amount: orderData.amount,
            currency: 'INR',
            name: 'Pagoda Hotel',
            description: selectedService.roomservice_name,
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              const paymentDetails = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              };

              try {
                const verificationResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                    },
                    body: JSON.stringify(paymentDetails),
                  }
                );

                if (verificationResponse.ok) {
                  toast.success('Payment successful and verified!');
                  window.location.href = '/roomservice-payment-succesful';
                } else {
                  const error = await verificationResponse.text();
                  console.error('Verification failed:', error);
                  toast.error('Payment verification failed.');
                }
              } catch (error) {
                console.error('Verification error:', error);
                toast.error('Error verifying payment. Please try again.');
              }
            },
            prefill: {
              name: `${guestData?.first_name || ''} ${guestData?.last_name || ''}`,
              email: guestData?.email || '',
              contact: guestData?.phone_number || '',
            },
            modal: {
              ondismiss: function () {
                console.log('Payment modal closed');
              },
            },
            theme: {
              color: '#3399cc',
            },
          };

          const rzp = new window.Razorpay(options);

          rzp.on('payment.failed', function (response) {
            console.error('Payment failed:', response.error);
            toast.error('Payment failed: ' + response.error.description);
          });

          rzp.open();
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Error processing payment. Please try again.');
      }
    } else if (paymentMethod === 'payLater') {
      toast.info('Payment deferred. Please pay at the front desk.');
      sessionStorage.removeItem('selectedService');
      sessionStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    } else if (paymentMethod === 'billToCompany') {
      toast.info(
        `Bill to company option selected. Charges will be billed to ${guestData?.company_name || 'your company'}.`
      );
      sessionStorage.removeItem('selectedService');
      sessionStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    }
  };

  const handleBookClick = (service) => {
    setSelectedService({
      ...service,
      category_name: service.name,
    });
    setFormData({ ...formData, serviceName: service.name });
    setIsModalOpen(true);
  };

  const handlePreview = () => {
    console.log('Preview data:', formData);
    alert('Preview functionality would show a summary here');
  };

  const handleBook = () => {
    console.log('Booking data:', formData);
    alert('Booking submitted successfully!');
    setIsModalOpen(false);
  };

  // Get tax display text
  const getTaxDisplayText = (value, valueType) => {
    if (!value || value === 0) return 'Tax not applicable';
    if (valueType === 'P') return `Including ${value}% GST`;
    if (valueType === 'F') return `Including ₹${value} tax`;
    return 'Including taxes';
  };

  // Calculate GST breakdown for percentage tax
  const getGSTBreakdown = (amount, value, valueType) => {
    if (valueType === 'P' && value > 0) {
      const totalTax = (amount * value) / 100;
      return {
        cgst: totalTax / 2,
        sgst: totalTax / 2,
        totalTax: totalTax,
      };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <Link
        to="/reservation/options/booking/welcome"
        className="absolute top-12 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </Link>

      <div className="px-10 mt-14 mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-2">Room Services</h1>
          <h2 className="text-md font-poppins font-semibold text-center mb-1 text-black">
            Enhance your stay with our premium in-room offerings designed for comfort, convenience,
            and celebration
          </h2>
          <hr className="border border-gray-300 w-1/4 mx-auto mb-4" />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} onBookClick={handleBookClick} />
          ))}
        </div>
      </div>

      {isModalOpen && (
        <BookingModal
          formData={formData}
          setFormData={setFormData}
          selectedService={selectedService}
          onClose={() => setIsModalOpen(false)}
          onPreview={handlePreview}
          onBook={handleBook}
          confirmServiceBooking={confirmServiceBooking}
        />
      )}

      {showPaymentModal && billingDetails && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
          <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200 relative">
            {/* Close button */}
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Complete Your Payment</h3>

            {/* Tax Information Display */}
            {taxDetails.value > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4 text-left">
                <p className="text-sm font-medium text-gray-700 mb-2">Tax Details:</p>
                <p className="text-sm text-gray-600">
                  {getTaxDisplayText(taxDetails.value, taxDetails.value_type)}
                </p>
                {taxDetails.value_type === 'P' && billingDetails && (
                  <div className="mt-2 text-xs text-gray-500">
                    <div className="flex justify-between">
                      <span>CGST ({taxDetails.value / 2}%):</span>
                      <span>
                        ₹
                        {((billingDetails.finalamount * taxDetails.value) / 100 / 2).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>SGST ({taxDetails.value / 2}%):</span>
                      <span>
                        ₹
                        {((billingDetails.finalamount * taxDetails.value) / 100 / 2).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </span>
                    </div>
                  </div>
                )}
                {taxDetails.value_type === 'F' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Fixed tax amount of ₹{taxDetails.value} applied
                  </p>
                )}
              </div>
            )}

            <p className="text-md text-gray-600 mb-6">
              Your booking has been successfully confirmed! Please select a payment method to
              proceed:
            </p>

            <p className="text-lg font-semibold text-gray-800 mb-4">
              Total Amount: ₹
              {billingDetails?.finalamount?.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) || '0.00'}
            </p>

            <div className="flex justify-center gap-4">
              {/* Pay Later option for leisure guests */}
              {!isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('payLater')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Pay Later
                </button>
              )}

              {/* Bill to Company option for corporate guests */}
              {isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('billToCompany')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Bill to Company
                </button>
              )}

              {/* Pay Now option for all guests */}
              <button
                onClick={() => handlePaymentOption('payNow')}
                className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default RoomServices;
// import { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack } from "react-icons/io";
// import ServiceCard from "./ServiceCard";
// import BookingModal from "./BookingModal";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const RoomServices = () => {
//   const [services, setServices] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedService, setSelectedService] = useState(null);
//   const [formData, setFormData] = useState({
//     serviceName: "",
//     itemName: "",
//     urgencyLevel: "Normal",
//     scheduleTime: "",
//     requestedTime: new Date().toISOString().slice(0, 16),
//     specialInstruction: "",
//   });

//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);

//   const bookingDetails =
//     JSON.parse(sessionStorage.getItem("bookingDetails")) || {};

//   // Fetch room services from API
//   useEffect(() => {
//     const fetchServices = async () => {
//       try {
//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/room-service-categories/`,
//           { headers: { Accept: "application/json" } }
//         );
//         const data = await response.json();
//         setServices(data); // ✅ Store all services, no filtering!
//       } catch (error) {
//         console.error("Error fetching room services:", error);
//       }
//     };

//     fetchServices();
//   }, []);

//   const confirmServiceBooking = async () => {
//     try {
//       // Add category_name to the query parameters
//       const url = new URL(`${BQ_BASE_URL}/bq/api/create_room_service_billing`);
//       url.searchParams.append("bookingid", bookingDetails.bookingid);
//       url.searchParams.append("category_name", selectedService.category_name); // Make sure selectedService has this property

//       const response = await fetch(url.toString(), {
//         method: "POST",
//         headers: {
//           Accept: "application/json",
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify([selectedService.roomservice_name]),
//       });

//       if (response.ok) {
//         const data = await response.json();
//         sessionStorage.setItem("billingDetails", JSON.stringify(data));
//         sessionStorage.setItem(
//           "selectedService",
//           JSON.stringify(selectedService)
//         );
//         toast.success("Service confirmed successfully");
//         closeConfirmationModal();
//         setShowPaymentModal(true);
//       } else {
//         const errorData = await response.json();
//         toast.error(errorData.detail?.[0]?.msg || "Failed to confirm service.");
//       }
//     } catch (error) {
//       toast.error("An error occurred while confirming the service.");
//     }
//   };

//   const closeConfirmationModal = () => {
//     setSelectedService(null);
//   };

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error("Error loading Razorpay script. Please try again.");
//       };
//       document.body.appendChild(script);
//     };

//     loadRazorpayScript();
//   }, []);

//   const handlePaymentOption = async (paymentMethod) => {
//     if (!razorpayLoaded) {
//       toast.error("Razorpay script is not loaded yet.");
//       return;
//     }

//     if (paymentMethod === "payNow") {
//       try {
//         // Retrieve selected service from localStorage
//         const selectedService = JSON.parse(
//           sessionStorage.getItem("selectedService")
//         );
//         if (!selectedService) {
//           toast.error("No service selected for payment.");
//           return;
//         }

//         // Retrieve billing details from localStorage
//         const billingDetails = JSON.parse(
//           sessionStorage.getItem("billingDetails")
//         );
//         if (!billingDetails || !billingDetails.billingid) {
//           toast.error("Billing details not found. Please try again.");
//           return;
//         }

//         const billingId = billingDetails.billingid;
//         const bookingId = bookingDetails.bookingid;

//         const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

//         const response = await fetch(apiUrl, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//         });

//         if (!response.ok) throw new Error("Failed to create payment order");

//         const orderData = await response.json();

//         if (orderData && orderData.razorpay_order_id) {
//           const options = {
//             key: REACT_APP_RAZORPAY_KE,
//             amount: orderData.amount,
//             currency: "INR",
//             name: "ABC Hotel",
//             description: selectedService.roomservices_name, // Show service name in payment
//             order_id: orderData.razorpay_order_id,
//             handler: async function (response) {
//               const paymentDetails = {
//                 razorpay_order_id: response.razorpay_order_id,
//                 razorpay_payment_id: response.razorpay_payment_id,
//                 razorpay_signature: response.razorpay_signature,
//               };

//               try {
//                 const verificationResponse = await fetch(
//                   `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                   {
//                     method: "POST",
//                     headers: {
//                       "Content-Type": "application/json",
//                       Accept: "application/json",
//                     },
//                     body: JSON.stringify(paymentDetails),
//                   }
//                 );

//                 if (verificationResponse.ok) {
//                   toast.success("Payment successful and verified!");
//                   window.location.href = "/roomservice-payment-succesful";
//                 } else {
//                   toast.error("Payment verification failed.");
//                 }
//               } catch (error) {
//                 console.error("Verification error:", error);
//                 toast.error("Error verifying payment. Please try again.");
//               }
//             },
//             prefill: {
//               name: `${bookingDetails.firstname} ${bookingDetails.lastname}`,
//               email: bookingDetails.emailid,
//               contact: bookingDetails.phonenumber.toString(),
//             },
//             theme: {
//               color: "#3399cc",
//             },
//           };

//           const rzp = new window.Razorpay(options);
//           rzp.open();
//         }
//       } catch (error) {
//         console.error("Payment error:", error);
//         toast.error("Error processing payment. Please try again.");
//       }
//     } else if (paymentMethod === "payLater") {
//       toast.info("Payment deferred. Please pay at the front desk.");
//       localStorage.removeItem("selectedService");
//       localStorage.removeItem("billingDetails");
//       setShowPaymentModal(false);
//     }
//   };

//   const handleBookClick = (service) => {
//     setSelectedService({
//       ...service,
//       category_name: service.name, // Since these are categories, their name IS the category name
//     });
//     console.log(selectedService);
//     setFormData({ ...formData, serviceName: service.name });
//     setIsModalOpen(true);
//   };

//   const handlePreview = () => {
//     console.log("Preview data:", formData);
//     alert("Preview functionality would show a summary here");
//   };

//   const handleBook = () => {
//     console.log("Booking data:", formData);
//     alert("Booking submitted successfully!");
//     setIsModalOpen(false);
//   };

//   return (
//     <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//       <Link
//         to="/reservation/options/booking/welcome"
//         className="absolute top-12 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
//       >
//         <IoMdArrowRoundBack size={32} />
//       </Link>

//       <div className="px-10 mt-14 mx-auto">
//         <div className="text-center  mb-10 ">
//           <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-2">
//             Room Services
//           </h1>
//           <h2 className="text-md font-poppins font-semibold text-center mb-1 text-black">
//             Enhance your stay with our premium in-room offerings designed for
//             comfort, convenience, and celebration.
//           </h2>
//           <hr className="border border-gray-300 w-1/4 mx-auto mb-4" />
//         </div>

//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
//           {services.map((service) => (
//             <ServiceCard
//               key={service.id}
//               service={service}
//               onBookClick={handleBookClick}
//             />
//           ))}
//         </div>
//       </div>

//       {isModalOpen && (
//         <BookingModal
//           formData={formData}
//           setFormData={setFormData}
//           selectedService={selectedService}
//           onClose={() => setIsModalOpen(false)}
//           onPreview={handlePreview}
//           onBook={handleBook}
//           confirmServiceBooking={confirmServiceBooking}
//         />
//       )}
//       {showPaymentModal && (
//         <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
//           <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
//             <h3 className="text-2xl font-semibold mb-4 text-gray-900">
//               Complete Your Payment
//             </h3>
//             <p className="text-md text-gray-600 mb-6">
//               Your booking has been successfully confirmed! Please select a
//               payment method to proceed:
//             </p>

//             <div className="flex justify-center gap-4">
//               <button
//                 onClick={() => handlePaymentOption("payLater")}
//                 className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
//               >
//                 Pay Later
//               </button>
//               <button
//                 onClick={() => handlePaymentOption("payNow")}
//                 className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
//               >
//                 Pay Now
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer />
//     </div>
//   );
// };

// export default RoomServices;
